import axios from 'axios';

// Determine the base URL based on environment
const getBaseURL = () => {
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  return process.env.REACT_APP_BASE_URL || '/api';
};

// Keep short-lived authorization token in memory only
let inMemoryAuthzToken = null;

// Create axios instance
const apiClient = axios.create({
  baseURL: getBaseURL(),
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // send cookies (identity) with requests
});

// Attach authorization token
apiClient.interceptors.request.use(
  async (config) => {
    // Skip adding Authorization header for signin and signup requests
    const url = config.url || '';
    if (
      url.includes('/iam/signin') ||
      url.includes('/iam/signup') ||
      url.includes('/iam/validate-invitation') ||
      url.includes('/iam/reset-password') ||
      url.includes('/iam/sso/')
    ) {
      return config;
    }
    if (inMemoryAuthzToken) {
      config.headers.Authorization = `Bearer ${inMemoryAuthzToken}`;
    } else {
      try {
        const newToken = await refreshAuthorizationToken();
        inMemoryAuthzToken = newToken;
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (refreshErr) {
        inMemoryAuthzToken = null;
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup')) {
          window.location.href = '/login';
        }
        throw refreshErr;
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.debug(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let pendingRequests = [];

async function refreshAuthorizationToken() {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingRequests.push({ resolve, reject });
    });
  }
  isRefreshing = true;
  try {
    // Uses axios directly to avoid the interceptor
    const res = await axios.post(`${getBaseURL()}/iam/authorize`, {}, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
      timeout: 5000,
    });
    const { authorization_token } = res.data || {};
    if (!authorization_token) throw new Error('No authorization token returned');

    inMemoryAuthzToken = authorization_token;

    pendingRequests.forEach(({ resolve }) => resolve(authorization_token));
    pendingRequests = [];
    return authorization_token;
  } catch (err) {
    pendingRequests.forEach(({ reject }) => reject(err));
    pendingRequests = [];
    throw err;
  } finally {
    isRefreshing = false;
  }
}

// Response interceptor to handle 401 and try one refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAuthorizationToken();
        inMemoryAuthzToken = newToken;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshErr) {
        inMemoryAuthzToken = null;
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/signup')) {
          window.location.href = '/login';
        }
      }
    } else if (error.response) {
      switch (error.response.status) {
        case 403:
          console.warn('Access forbidden:', error.response.data?.message || 'Insufficient permissions');
          break;
        case 500:
          console.error('Server error:', error.response.data?.message || 'Internal server error');
          break;
        default:
          break;
      }
    } else if (error.request) {
      console.error('Network/CORS error:', error.message);
      if (process.env.NODE_ENV === 'development') {
        console.error(
          'CORS Error Debug Info:\n' +
          '- Make sure your backend allows CORS from your frontend origin\n' +
          '- Check that the proxy in package.json is configured correctly\n' +
          '- Verify the backend is running on the expected port'
        );
      }
    }

    return Promise.reject(error);
  }
);

export function setAuthorizationToken(token) {
  inMemoryAuthzToken = token;
}

// For non-axios callers (e.g. the fetch()-based SSE client in ChatPanel.js)
// that need to set their own Authorization header. Mirrors the interceptor's
// refresh-if-absent behavior above.
export async function getAuthorizationToken() {
  if (inMemoryAuthzToken) {
    return inMemoryAuthzToken;
  }
  return refreshAuthorizationToken();
}

export default apiClient;